import { localDateOf } from '@his/shared';

export type PayerMode = 'self' | 'insurance';

export interface PayerChoice {
  mode: PayerMode;
  payerId: string | null;
  coverageId: string | null;
}

export interface PayerRequest {
  payerId: string | null;
  coverageId: string | null;
}

export interface LastVisitPayer {
  payerId: string | null;
  coverageId?: string | null;
}

export interface CoverageValidity {
  validFrom: string | null;
  validTo: string | null;
  isActive: boolean;
  deletedAt?: string | null;
}

export interface CoverageIds {
  id: string;
  payerId: string;
}

export const SELF_PAY: PayerChoice = { mode: 'self', payerId: null, coverageId: null };

export const INSURANCE_PENDING: PayerChoice = { mode: 'insurance', payerId: null, coverageId: null };

export function insuranceChoice(coverage: CoverageIds): PayerChoice {
  return { mode: 'insurance', payerId: coverage.payerId, coverageId: coverage.id };
}

export function bookingDayOf(date: string, at: string | null | undefined, timeZone: string): string {
  const instant = at ? Date.parse(at) : Number.NaN;
  return Number.isNaN(instant) ? date : localDateOf(new Date(instant), timeZone);
}

export function coverageValidOn(coverage: CoverageValidity, day: string): boolean {
  if (!coverage.isActive || coverage.deletedAt) return false;
  if (coverage.validFrom && coverage.validFrom.slice(0, 10) > day) return false;
  if (coverage.validTo && coverage.validTo.slice(0, 10) < day) return false;
  return true;
}

export function defaultPayerChoice(valid: readonly CoverageIds[], lastVisit: LastVisitPayer | null | undefined): PayerChoice {
  if (!lastVisit?.payerId) return SELF_PAY;
  const sameCoverage = lastVisit.coverageId ? valid.find((coverage) => coverage.id === lastVisit.coverageId) : undefined;
  if (sameCoverage) return insuranceChoice(sameCoverage);
  const samePayer = valid.filter((coverage) => coverage.payerId === lastVisit.payerId);
  if (samePayer.length === 1) return insuranceChoice(samePayer[0]);
  return valid.length > 0 ? INSURANCE_PENDING : SELF_PAY;
}

export function insuranceChoiceFor(valid: readonly CoverageIds[]): PayerChoice {
  return valid.length === 1 ? insuranceChoice(valid[0]) : INSURANCE_PENDING;
}

export function payerChoiceComplete(choice: PayerChoice): boolean {
  return choice.mode === 'self' || Boolean(choice.coverageId);
}

export function payerRequest(choice: PayerChoice): PayerRequest {
  return choice.mode === 'self' ? { payerId: null, coverageId: null } : { payerId: choice.payerId, coverageId: choice.coverageId };
}

export function samePayerChoice(a: PayerChoice, b: PayerChoice): boolean {
  return a.mode === b.mode && a.payerId === b.payerId && a.coverageId === b.coverageId;
}

export function choiceStillOffered(choice: PayerChoice, valid: readonly { id: string }[]): boolean {
  return !choice.coverageId || valid.some((coverage) => coverage.id === choice.coverageId);
}
